<template>
  <div class="container-bg">
    <button v-if="isVisibleButtonBack" class="icon floating glass rounded" @click="onClickButtonBack">
      <IconChevronLeft class="icon-back" />
    </button>
    <div :class="['container', { 'no-bg': !backgroundImageUrl }]">
      <div class="image-bg-uploaded" :style="{ backgroundImage: `url(${backgroundImageUrl})` }">
        <!--로고-->
        <img class="scon-ai-logo" :src="getAssetImageUrl('common/logos/logo-sconai-white.png')" />
      </div>

      <main>
        <div class="entrance-container">
          <!--로고 -->
          <EntranceLogo />

          <!--정보-->
          <div class="contents-container">
            <!--언어 선택-->
            <EntranceDropdownLanguage v-model:language-selected="languageSelected" :is-visible-error-language />
            <!--닉네임-->
            <EntranceInputNickname
              v-model:nickname-input="nicknameInput"
              v-model:is-visible-error-nickname="isVisibleErrorNickname"
              @pressed-enter="goToCallView"
            />
            <ButtonProcess
              full-width
              :loading="loading"
              :class="{ disabled: isDisabledButtonProcess }"
              @click="goToCallView"
            >
              {{ $t('entrance') }}
            </ButtonProcess>
          </div>

          <!--목소리 등록-->
          <EntranceSectionMyVoice
            :show-connected-device="showConnectedDevice"
            :device-recognition-error="microphoneStore.microphones.length <= 0"
            :any-registered="anyRegistered"
            :current-device-label="microphoneStore.selectedMicrophone.label"
            :registered-device-id="registeredDeviceId"
            :qr-code-info="qrCodeInfo"
            :delete-voice="deleteVoice"
            :show-register-voice-modal="showRegisterVoiceModal"
            @open="toggleVoiceNewModal"
          />

          <!--다운로드 + sconai-->
          <EntranceFooter :is-using-download="isUsingDownload" :is-using-logout="isUsingLogout" />
        </div>
      </main>
    </div>
    <AddVoiceModal
      v-if="showVoiceModal"
      :device="microphoneStore.selectedMicrophone"
      :long-voice="
        ['CONSULTATION_CALL', 'GENERAL_CALL_PARTNER', 'CONFERENCE_CALL_PARTNER'].includes(qrCodeInfo.qrCodeType)
      "
      @close="closeVoiceModal"
    />
    <ModalAddVoice v-if="showVoiceNewModal" @close="toggleVoiceNewModal" @start="goToCallView" />
    <CallHistoryModal
      v-if="showCallHistoryModal"
      :code="code"
      :call-type="callStore.qrCodeInfo.callType"
      @close="showCallHistoryModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { watchDeep } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import api from '@/api';
import { fetchAuthCheck, nextChatId, fetchQrCode } from '@/api/apiService';
import AddVoiceModal from '@/components/domains/admin/common/AddVoiceModal.vue';
import ModalAddVoice from '@/components/domains/admin/common/ModalAddVoice.vue';
import EntranceDropdownLanguage from '@/components/domains/chat/call-entrance/EntranceDropdownLanguage.vue';
import EntranceFooter from '@/components/domains/chat/call-entrance/EntranceFooter.vue';
import EntranceInputNickname from '@/components/domains/chat/call-entrance/EntranceInputNickname.vue';
import EntranceLogo from '@/components/domains/chat/call-entrance/EntranceLogo.vue';
import EntranceSectionMyVoice from '@/components/domains/chat/call-entrance/EntranceSectionMyVoice.vue';
import CallHistoryModal from '@/components/domains/internal/call-history/CallHistoryModal.vue';
import IconChevronLeft from '@/components/icons/IconChevronLeft.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { useButtonToggleMic } from '@/composables/domains/chat/useButtonToggleMic';
import { useSocketCall } from '@/composables/domains/chat/useSocketCall';
import { useAccount } from '@/composables/shared/useAccount';
import { useButtonBackIos } from '@/composables/shared/useButtonbackIos';
import { useNotification } from '@/composables/shared/useNotification';
import { getLanguageByCode } from '@/static/languages';
import { useAllTranslations } from '@/static/messageBylanguages.js';
import { useAlertStore } from '@/stores/alert';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useMicrophoneStore } from '@/stores/microphone';
import { SocketState, useSocketStore } from '@/stores/socket';
import { UserType, useUserStore } from '@/stores/user';
import { Language } from '@/types/components';
import { ConnectionInfo } from '@/types/socket';
import { base64UrlSafeToString, stringToBase64UrlSafe } from '@/utils/encryptor.js';
import { isQrAccessType } from '@/utils/enums';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import redirectToLoginPage from '@/utils/redirectToLoginPage';

const router = useRouter();
const route = useRoute();
const socketStore = useSocketStore();
const userStore = useUserStore();
const alertStore = useAlertStore();
const callStore = useCallStore();
const audioStore = useAudioStore();
const microphoneStore = useMicrophoneStore();
const translations = useAllTranslations();

const { qrCodeInfo } = storeToRefs(callStore);
const { userInfo } = storeToRefs(userStore);
const { selectedIsVoiceRegistered, selectedRegisteredDeviceId } = storeToRefs(socketStore);

const { t } = useI18n();
const { isVisibleButtonBack, onClickButtonBack } = useButtonBackIos();
const { logoutAndGoToLogin } = useAccount();
const { isVisibleMic } = useButtonToggleMic();

// Data Parameters
const code = ref<string>(route.params.code);
const chatId = ref<string>('');
const registeredDeviceId = ref(null);
const anyRegistered = ref(false);
const nicknameInput = ref('');
const showVoiceModal = ref(false);
const showVoiceNewModal = ref(false);
const showCallHistoryModal = ref(false);
const isReady = ref(false);
const logoImageUrl = ref('');
const qrCodeGroupName = ref('');
const title = ref('');
const languageSelected = ref<Language | null>(null);
const loading = ref(false);
const showConnectedDevice = ref(false);
const backgroundImageUrl = ref<string>('/images/bg-entrance.gif');
let checkDeviceChangeIntervalId;
//for DEBUG
const accessType = ref(null);
const isVisibleErrorNickname = ref(false); // 닉네임 입력 에러 보임
const isVisibleErrorLanguage = ref(false); // 언어 선택 입력 에러 보임
// 드랍다운 관련 상태 및 참조
const showDropdown = ref(false);
const dropdownContainerRef = ref(null);

const connectionInfo = computed<Partial<ConnectionInfo>>(() => {
  return (
    {
      GENERAL: {
        chatId: chatId.value,
      },
      CONFERENCE: {
        callType: 'CONFERENCE',
        chatId: chatId.value,
      },
      MEETING: {
        chatId: chatId.value,
        callType: 'MEETING',
        isTssOn: '0',
        voiceId: '0',
      },
    }[callStore.qrCodeInfo.callType] || ({ chatId: chatId.value } as Partial<ConnectionInfo>)
  );
});

const isDisabledButtonProcess = computed(() => {
  return !nicknameInput.value.trim() || !languageSelected.value;
});

const isUsingLogout = computed<boolean>(() => {
  return !QRCodeType.isGuest(qrCodeInfo.value.qrCodeType);
});

const isUsingDownload = computed<boolean>(() => {
  const { qrCodeType, options } = qrCodeInfo.value;
  const { storeAgreement = false } = userStore.userInfo || {};
  return storeAgreement && !options.directMessage && !QRCodeType.isGuest(qrCodeType);
});
const { connectToWebsocket } = useSocketCall();

const reconnect = async () => {
  const { success, data } = await api.get(`/api/chat/business/call/${code.value}/next`, { responseType: 'text' });

  if (success) {
    chatId.value = data;
    await goToCallView();
  }
};

const initDeviceAndGoToCallView = async () => {
  await fetchRegisteredDeviceId();
  await goToCallView();
};

const goToCallView = async () => {
  await deleteGuestAuthToken();
  isVisibleErrorLanguage.value = !languageSelected.value;
  isVisibleErrorNickname.value = !nicknameInput.value.trim();

  if (isVisibleErrorLanguage.value || isVisibleErrorNickname.value) return;

  if (code.value === 'xCCDJ') {
    alertStore.alert(
      {
        title: translations.guide.value,
        message: translations.alert_server_maintenance.value,
      }, //앱을 위한거라 통역 필요없음
      null
    );
    return;
  }

  if (userInfo.value.userType !== 'GUEST' && QRCodeType.isGuest(qrCodeInfo.value.qrCodeType)) {
    alertLoginGuestAccess(initDeviceAndGoToCallView);
    return;
  }

  loading.value = true;
  socketStore.connectionResponse = 0;

  callStore.updateCode(code.value);
  callStore.updateUserInfo(userInfo.value, nicknameInput.value);

  if (!registeredDeviceId.value) {
    await fetchRegisteredDeviceId();
  }
  callStore.updateDeviceId(registeredDeviceId.value);

  selectedIsVoiceRegistered.value = registeredDeviceId.value ? '1' : '0';
  selectedRegisteredDeviceId.value = registeredDeviceId.value ?? 0;

  userInfo.value.nickname = nicknameInput.value;
  localStorage.setItem('userInfo', JSON.stringify(userInfo.value));
  await connectToWebsocket(true, connectionInfo.value);
};

const showRegisterVoiceModal = () => {
  showVoiceModal.value = true;
};

const openCallHistoryModal = () => {
  showCallHistoryModal.value = true;
};

const getUserInfo = async () => {
  await userStore.fetchUserInfo();
  if (userInfo.value.nickname && userInfo.value.nickname !== '' && userInfo.value.userType !== 'GUEST') {
    nicknameInput.value = base64UrlSafeToString(userInfo.value.nickname);
  }
};

const closeVoiceModal = async (deviceId) => {
  if (deviceId) {
    registeredDeviceId.value = deviceId;
    anyRegistered.value = true;
    showConnectedDevice.value = true;
  }
  showVoiceModal.value = false;
  await microphoneStore.initMicrophoneStore({
    isUsingMicWithVoice: true,
    isUsingSpecificGain: true,
    isUsingDeviceAutoUpdate: true,
  });
};

const toggleVoiceNewModal = () => {
  showVoiceNewModal.value = !showVoiceNewModal.value;
};

const goToLoginIfNoAuth = (partnerType?: string) => {
  if (QRCodeType.isEmployee(qrCodeInfo.value.qrCodeType) && UserType.isUser(userInfo.value.userType)) {
    const afterLoginPath = `${location.pathname}&partnerCode=${callStore.qrCodeInfo.partnerCode}`;

    redirectToLoginPage(afterLoginPath, partnerType);
    return false;
  } else {
    return true;
  }
};

const checkDemoAuthAndDebugView = async (partnerId, partnerType) => {
  if (!goToLoginIfNoAuth(partnerType)) {
    return false;
  }
  if (QRCodeType.isConsultationType(qrCodeInfo.value.qrCodeType)) {
    return true;
  }
  if (localStorage.getItem('isTester')) {
    return true;
  }

  const userPartnerId = localStorage.getItem('partnerId');

  if (isQrAccessType.isDemo(accessType.value)) {
    if (partnerId === userPartnerId && localStorage.getItem('isTester')) {
      return true;
    }
    if (QRCodeType.isGuest(qrCodeInfo.value.qrCodeType)) {
      return true;
    }
  }

  if (QRCodeType.isGuest(qrCodeInfo.value.qrCodeType)) {
    return true;
  }
  if (partnerId == userPartnerId) {
    return true;
  }
  alertStore.alert(
    {
      title: t('guide'),
      message: t('wrong_approach'),
    },
    {
      confirm: async () => {
        await logoutAndGoToLogin();
      },
    }
  );
  return false;
};

const handleBackButton = (event) => {
  if (showVoiceModal.value) {
    history.pushState(history.state, '', location.href); // 현재 페이지 상태 복구
    showVoiceModal.value = false;
  } else {
    history.back();
  }
};

async function fetchRegisteredDeviceId() {
  const label = microphoneStore.selectedMicrophone.label;

  if (label) {
    const { success, data } = await api.get(
      `/api/chat/business/call/voice/device/registered?deviceLabel=${stringToBase64UrlSafe(label)}`
    );

    if (success) {
      registeredDeviceId.value = data.deviceId;
      anyRegistered.value = data.anyRegistered;
    }
  }
}

const closeDropdown = () => {
  showDropdown.value = false;
};

const handleClickOutside = (event) => {
  if (dropdownContainerRef.value && !dropdownContainerRef.value.contains(event.target)) {
    closeDropdown();
  }
};

// 등록된 음성 삭제
const deleteVoice = async () => {
  const deviceIdToDelete = registeredDeviceId.value;
  const callbackConfirm = async () => {
    await fetchRegisteredDeviceId();
  };
  alertDeleteVoice(deviceIdToDelete, callbackConfirm);
};

const deleteGuestAuthToken = async () => {
  if (QRCodeType.isGuest(qrCodeInfo.value.qrCodeType) && localStorage.getItem('authToken')) {
    const { errorCode } = await fetchAuthCheck();
    if (errorCode === 4010) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('userId');
    }
  }
};

watch(showDropdown, (newVal) => {
  if (newVal) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

// call listener
watch(
  [() => socketStore.connectionResponse, () => socketStore.socketState],
  async ([connectionResponse, socketState]) => {
    console.log(`connectionResponse: ${connectionResponse}, socketState: ${socketState}, ${loading.value}`);

    if (!loading.value) return;

    if (connectionResponse === -1 || connectionResponse === 0) {
      // 연결 시도
      return;
    }
    if (connectionResponse === 1 && socketState !== SocketState.CLOSED) return;

    loading.value = false;
    if (callStore.qrCodeInfo.code === 'xCCDJ') {
      alertNetworkError();
      return;
    }
    if (connectionResponse === 9999 && socketState === SocketState.CLOSED) {
      alertNetworkError();
      return;
    }

    if (connectionResponse === 1 && socketState === SocketState.CLOSED) {
      alertNetworkError();
    } else if (connectionResponse === 2) {
      // 연결 성공
      socketStore.endOfDemo = false;
      audioStore.clearAll();
      socketStore.resetAttendees(); // 참여자 목록 초기화

      if (qrCodeInfo.value.qrCodeType.includes('MEETING')) {
        router.push({
          name: 'chat-meeting',
          params: { code: code.value },
        });
      } else if (qrCodeInfo.value.qrCodeType.includes('CONFERENCE')) {
        router.push({
          name: 'chat-conference',
          params: { code: code.value },
        });
      } else {
        router.push({
          name: 'chat-general',
          params: { code: code.value },
        });
      }
    } else if (connectionResponse === 1029) {
      // 사용량 초과
      alertExceededUsageLimit();
    } else if (connectionResponse === 1016) {
      // 잘못된 코드 요청
      alertInvalidCode();
    } else if (connectionResponse === 1030) {
      // 동시접속시,
      alertEvictMyself(goToCallView);
    } else if (QRCodeType.isConsultationType(callStore.qrCodeInfo.qrCodeType)) {
      if (connectionResponse === 4010) {
        // 인증 실패
        if (localStorage.getItem('authToken')) {
          await logoutAndGoToLogin();
        } else {
          await getUserInfo();
          await goToCallView();
        }
      } else if (connectionResponse === 1026) {
        // 이미 통역중 일때,
        alertAlreadyCalling(true);
      } else if (connectionResponse === 1027) {
        // 이미 종료된 통역일 때,
        alertAlreadyEndedCall(true);
      } else {
        alertNetworkError();
      }
    } else {
      // FAIR / CONFERENCE Error
      if (connectionResponse === 4010) {
        // 인증 실패, 테완
        if (QRCodeType.isEmployee(callStore.qrCodeInfo.qrCodeType)) {
          logoutAndGoToLogin();
        } else {
          await getUserInfo();
          goToCallView();
        }
      } else if (connectionResponse === 4030) {
        alertBasic({ title: t('entry_limited') }, () => {
          loading.value = false;
        });
      } else if (connectionResponse === 1023) {
        // 호스트 접속이 안되어있을 때, 테완
        alertNoEmployee();
      } else if (connectionResponse === 1026) {
        // 동시 접속자가 초과하였을 때, 테완
        alertExceededNumOfMembers();
      } else if (connectionResponse === 1027) {
        // 이미 종료된 통역일 때, 테완
        if (UserType.isPartner(callStore.userInfo.userType)) {
          alertAlreadyEndedCallWithReconnect(reconnect);
        } else {
          alertAlreadyEndedCall();
        }
      } else {
        // 나머지
        alertNetworkError();
      }
    }
  }
);

onMounted(async () => {
  history.pushState(history.state, '', location.href);
  window.addEventListener('popstate', handleBackButton);

  if (code.value === 'call') {
    history.go(-2);
    return;
  }
  const { success = false, data = {} } = (await fetchQrCode(code.value)) || {};
  if (!success) {
    alertStore.alert(
      {
        title: t('call_entrance_invalid_qr_code_title'),
        message: t('call_entrance_invalid_qr_code_inquiry'),
      },
      {
        confirm: () => {
          window.location.href = 'https://www.sconchat.com/';
        },
      },
      false
    );
    return;
  }

  callStore.init();
  callStore.updateCode(code.value);
  callStore.setQrCodeInfo(data);

  const { success: isChatIdSuccess, data: nextId } = (await nextChatId(code.value)) || {};

  if (!isChatIdSuccess) {
    await reconnect();
  } else {
    chatId.value = nextId;
  }

  qrCodeGroupName.value = qrCodeInfo.value?.groupName || '';
  logoImageUrl.value = data?.logoImageUrl;
  // backgroundImageUrl.value = response.data.backgroundImageUrl || null;
  title.value = data?.title || 'SCON AI';
  accessType.value = data.accessType;

  await deleteGuestAuthToken();
  // demoPreProcess();

  await getUserInfo();

  //통역 타입에 따른 ui
  switch (qrCodeInfo.value.qrCodeType) {
    case QRCodeType.CONSULTATION_CALL:
      if (localStorage.getItem('authToken')) {
        nicknameInput.value = 'Host';
      } else {
        nicknameInput.value = 'Guest';
      }
      break;
    case QRCodeType.GENERAL_CALL_PARTNER:
    case QRCodeType.MEETING_CALL_PARTNER:
    case QRCodeType.CONFERENCE_CALL_PARTNER:
      break;
    case QRCodeType.GENERAL_CALL_GUEST:
    case QRCodeType.MEETING_CALL_GUEST:
    case QRCodeType.CONFERENCE_CALL_GUEST:
      nicknameInput.value = `GUEST${Math.floor(Math.random() * Date.now()) - 10000000}`;
      userInfo.value.nickname = nicknameInput.value;
      break;
    default:
      break;
  }

  const result = await checkDemoAuthAndDebugView(qrCodeInfo.value.partnerId, qrCodeInfo.value.partnerType);
  if (!result) {
    return;
  }

  //after init
  isReady.value = true;

  if (route.query.showVoiceRegister) {
    showRegisterVoiceModal();
  }

  const userLanguageCode =
    userStore.userInfo.languageCode || localStorage.getItem('language') || userStore.pageLanguage;
  languageSelected.value = getLanguageByCode(userLanguageCode);
  audioStore.selectLanguage(userLanguageCode);

  // 마이크 설정
  if (isVisibleMic.value) {
    await microphoneStore.initMicrophoneStore({
      isUsingMicWithVoice: true,
      isUsingSpecificGain: true,
      isUsingDeviceAutoUpdate: true,
    });
    await fetchRegisteredDeviceId();

    // 마이크 권한이 없을 경우, 권한을 요청합니다.
    if (!microphoneStore.permissionGranted) {
      await microphoneStore.ensurePermissions();
    }
  }
});

watchDeep(
  () => microphoneStore.selectedMicrophone,
  async (newMic, oldMic) => {
    if (isVisibleMic.value && newMic.label !== oldMic.label) {
      await fetchRegisteredDeviceId();
    }
  }
);

onUnmounted(async () => {
  window.removeEventListener('popstate', handleBackButton);
  if (checkDeviceChangeIntervalId) {
    clearInterval(checkDeviceChangeIntervalId);
  }
  await microphoneStore.initMicrophoneStore();
});

const {
  alertBasic,
  alertExceededUsageLimit,
  alertInvalidCode,
  alertEvictMyself,
  alertAlreadyCalling,
  alertAlreadyEndedCall,
  alertAlreadyEndedCallWithReconnect,
  alertNetworkError,
  alertNoEmployee,
  alertExceededNumOfMembers,
  alertLoginGuestAccess,
  alertLogout,
  alertDeleteVoice,
} = useNotification();
</script>

<style scoped lang="scss">
.icon-back {
  width: 1.5rem;
  height: 1.5rem;
}

.container-bg {
  background: var(--surface-background);
}

.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  height: 100dvh;
}

.container.no-bg {
  height: 100dvh;
  justify-content: center;
  align-items: center;
  background-color: var(--surface-default);
}

.scon-ai-logo {
  display: none;
}

main {
  display: flex;
  position: absolute;
  flex-direction: column;
  width: 90%;
  max-width: 30rem;
  height: fit-content;
  padding: 2rem 0;
  align-items: center;
  background-color: var(--surface-glass-dimmer);
  border-radius: 16px;
  box-shadow: $shadow-big;
}

.image-bg-uploaded {
  width: 100%;
  flex-grow: 1;
  max-height: 100%;
  background-color: var(--surface-default);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
}

.entrance-container {
  display: flex;
  flex-direction: column;
  width: 85%;
  max-width: 21rem;
  height: fit-content;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  position: relative;
}

.contents-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
}

.contents-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.25rem;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  white-space: nowrap;
}

/* 드랍다운 스타일 */
.dropdown-trigger {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.75rem;
  background-color: var(--surface-default);
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-trigger:hover,
.dropdown-trigger:active,
.dropdown-trigger.active {
  background-color: var(--surface-default-hover);
}

::v-deep(.dropdown-content) {
  width: 77dvw;
  max-width: 21rem;
}

.room-name {
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: var(--surface-default);
  border-radius: 8px;
}

.voice-control-wrap {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  gap: 0.125rem;
}

.entrance-container .link-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  position: relative;
  flex: 1;
  flex-grow: 1;
}

.info-sconai {
  display: flex;
  gap: 0.2rem;
  color: var(--color-gray-100);
  text-wrap: nowrap;
}

.foot {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
}

.voice-container {
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: var(--surface-primary-dimmer);
  outline: 1px solid var(--border-primary-dim);
}

.voice-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  text-align: center;
}

.voice-text-wrap {
  position: relative;
  display: flex;
  align-items: center;
  text-align: center;
}

.device-wrap {
  display: flex;
  flex-direction: column;
  width: fit-content;
  min-width: 7rem;
  align-items: center;
  gap: 0.125rem;
  padding: 0.325rem 1rem;
  background-color: var(--border-primary);
  border-radius: 8px;
}

@media (min-width: 768px) {
  main {
    width: 30rem;
    height: fit-content;
    align-items: center;
    border-radius: 16px;
  }

  .container {
    height: 100%;
    flex-direction: row;
  }

  .container.no-bg {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: transparent;
  }

  .image-bg-uploaded {
    width: 35%;
    height: 100dvh;
    max-height: unset;
  }

  .container.no-bg main {
    width: 25rem;
    height: 18.125rem;
    justify-content: center;
    padding: 2rem;
    border-radius: 8px;
  }

  .container.no-bg .button-call {
    width: 10.25rem;
  }
}

@media (min-width: 1024px) {
  main {
    left: 50%;
  }

  .scon-ai-logo {
    display: flex;
    position: absolute;
    justify-content: center;
    margin: auto 0;
    padding: 0.5rem 0.75rem 0.35rem 0.75rem;
    top: 50%;
    right: 55%;
    width: auto;
    height: 3.25rem;
    border-radius: 8px;
    background-color: var(--surface-glass-lightdimmer);
  }
}
</style>
